import BigNumber from 'bignumber.js';
import Big from 'big.js';
import { PoolInfo } from '../swapV3';
import { TokenMetadata } from '../ft-contract';
import { toPrecision, toReadableNumber } from '../../utils/numbers';

/**
 * calculate price by point
 * @param pointDelta
 * @param point
 * @param decimalRate tokenX/tokenY
 * @returns
 */
export function getPriceByPoint(point: number, decimalRate: number) {
  const price = Math.pow(CONSTANT_D, point) * decimalRate;
  const price_handled = new BigNumber(price).toFixed();
  return price_handled;
}

/**
 * calculate point by price
 * @param pointDelta
 * @param price
 * @param decimalRate tokenY/tokenX
 * @returns
 */
export function getPointByPrice(
  pointDelta: number,
  price: string,
  decimalRate: number,
  noNeedSlot?: boolean
) {
  const point = Math.log(+price * decimalRate) / Math.log(CONSTANT_D);
  const point_int = Math.round(point);
  let point_int_slot = point_int;
  if (!noNeedSlot) {
    point_int_slot = Math.round(point_int / pointDelta) * pointDelta;
  }
  if (point_int_slot < POINTLEFTRANGE) {
    return POINTLEFTRANGE;
  } else if (point_int_slot > POINTRIGHTRANGE) {
    return 800000;
  }
  return point_int_slot;
}

/**
 * calculate point by price
 * @param pointDelta
 * @param price
 * @param decimalRate tokenY/tokenX
 * @returns
 */
export function getSlotPointByPoint(pointDelta: number, point: number) {
  let point_int_slot = Math.round(point / pointDelta) * pointDelta;
  if (point_int_slot < POINTLEFTRANGE) {
    return POINTLEFTRANGE;
  } else if (point_int_slot > POINTRIGHTRANGE) {
    return 800000;
  }
  return point_int_slot;
}

export const CONSTANT_D = 1.0001;

export const POINTLEFTRANGE = -800000;

export const POINTRIGHTRANGE = 800000;

export function getXAmount_per_point_by_Lx(liquidity: string, point: number) {
  const xAmount = new BigNumber(liquidity)
    .dividedBy(Math.pow(Math.sqrt(CONSTANT_D), point))
    .toFixed();
  return xAmount;
}

export function getYAmount_per_point_by_Ly(liquidity: string, point: number) {
  const yAmount = new BigNumber(liquidity)
    .multipliedBy(Math.pow(Math.sqrt(CONSTANT_D), point))
    .toFixed();
  return yAmount;
}

/**
 *
 * @param amount  NonDivisible
 * @param point
 * @returns
 */
export function get_Lx_per_point_by_XAmount(amount: string, point: number) {
  const liquidity = Big(amount)
    .mul(Math.pow(Math.sqrt(CONSTANT_D), point))
    .toFixed();
  return liquidity;
}

/**
 *
 * @param amount NonDivisible
 * @param point
 * @returns
 */
export function get_Ly_per_point_by_YAmount(amount: string, point: number) {
  const liquidity = Big(amount)
    .div(Math.pow(Math.sqrt(CONSTANT_D), point))
    .toFixed();
  return liquidity;
}

export function get_total_value_by_liquidity_amount_dcl({
  left_point,
  right_point,
  poolDetail,
  amount,
  price_x_y,
  metadata_x_y,
}: {
  left_point: number;
  right_point: number;
  poolDetail: PoolInfo;
  amount: string;
  price_x_y: Record<string, string>;
  metadata_x_y: Record<string, TokenMetadata>;
}) {
  const [tokenX, tokenY] = Object.values(metadata_x_y);
  const [priceX, priceY] = Object.values(price_x_y);
  const { current_point } = poolDetail;
  let total_x = '0';
  let total_y = '0';
  //  in range
  if (current_point >= left_point && right_point > current_point) {
    const tokenYAmount = getY(left_point, current_point, amount, tokenY);
    const tokenXAmount = getX(current_point + 1, right_point, amount, tokenX);
    const { amountx, amounty } = get_X_Y_In_CurrentPoint(
      tokenX,
      tokenY,
      amount,
      poolDetail
    );
    total_x = new BigNumber(tokenXAmount).plus(amountx).toFixed();
    total_y = new BigNumber(tokenYAmount).plus(amounty).toFixed();
  }
  // only y token
  if (current_point >= right_point) {
    const tokenYAmount = getY(left_point, right_point, amount, tokenY);
    total_y = tokenYAmount;
  }
  // only x token
  if (left_point > current_point) {
    const tokenXAmount = getX(left_point, right_point, amount, tokenX);
    total_x = tokenXAmount;
  }
  const total_price_x = new BigNumber(total_x).multipliedBy(priceX);
  const total_price_y = new BigNumber(total_y).multipliedBy(priceY);
  return total_price_x.plus(total_price_y).toFixed();
}

export function getY(
  leftPoint: number,
  rightPoint: number,
  liquidity: string,
  token: TokenMetadata
) {
  const tokenYAmount = new BigNumber(liquidity).multipliedBy(
    (Math.pow(Math.sqrt(CONSTANT_D), rightPoint) -
      Math.pow(Math.sqrt(CONSTANT_D), leftPoint)) /
      (Math.sqrt(CONSTANT_D) - 1)
  );
  return tokenYAmount.shiftedBy(-token.decimals).toFixed();
}

export function getX(
  leftPoint: number,
  rightPoint: number,
  liquidity: string,
  token: TokenMetadata
) {
  const tokenXAmount = new BigNumber(liquidity).multipliedBy(
    (Math.pow(Math.sqrt(CONSTANT_D), rightPoint - leftPoint) - 1) /
      (Math.pow(Math.sqrt(CONSTANT_D), rightPoint) -
        Math.pow(Math.sqrt(CONSTANT_D), rightPoint - 1))
  );
  return tokenXAmount.shiftedBy(-token.decimals).toFixed();
}

/**
 *
 * @param leftPoint
 * @param rightPoint
 * @param token_x_amount NonDivisible
 */
export function getLByTokenX(
  leftPoint: number,
  rightPoint: number,
  token_x_amount: string
) {
  const liquidity = Big(token_x_amount)
    .div(
      (Math.pow(Math.sqrt(CONSTANT_D), rightPoint - leftPoint) - 1) /
        (Math.pow(Math.sqrt(CONSTANT_D), rightPoint) -
          Math.pow(Math.sqrt(CONSTANT_D), rightPoint - 1))
    )
    .toFixed(0);
  return liquidity;
}

/**
 *
 * @param leftPoint
 * @param rightPoint
 * @param token_y_amount NonDivisible
 * @returns
 */
export function getLByTokenY(
  leftPoint: number,
  rightPoint: number,
  token_y_amount: string
) {
  const liquidity = Big(token_y_amount)
    .div(
      (Math.pow(Math.sqrt(CONSTANT_D), rightPoint) -
        Math.pow(Math.sqrt(CONSTANT_D), leftPoint)) /
        (Math.sqrt(CONSTANT_D) - 1)
    )
    .toFixed(0);
  return liquidity;
}

export function get_X_Y_In_CurrentPoint(
  tokenX: TokenMetadata,
  tokenY: TokenMetadata,
  liquidityToRemove: string,
  poolDetail: PoolInfo
) {
  const { liquidity, liquidity_x, current_point } = poolDetail;
  const liquidity_y_big = new BigNumber(liquidity).minus(liquidity_x);
  let liquidityY = '0';
  let liquidityX = '0';
  // only remove y
  if (liquidity_y_big.isGreaterThanOrEqualTo(liquidityToRemove)) {
    liquidityY = liquidityToRemove;
  } else {
    // have x and y
    liquidityY = liquidity_y_big.toFixed();
    liquidityX = new BigNumber(liquidityToRemove).minus(liquidityY).toFixed();
  }
  const amountX = getXAmount_per_point_by_Lx(liquidityX, current_point);
  const amountY = getYAmount_per_point_by_Ly(liquidityY, current_point);
  const amountX_read = new BigNumber(amountX)
    .shiftedBy(-tokenX.decimals)
    .toFixed();
  const amountY_read = new BigNumber(amountY)
    .shiftedBy(-tokenY.decimals)
    .toFixed();
  return { amountx: amountX_read, amounty: amountY_read };
}

/**
 * calculate bin point by price
 * @param pointDelta
 * @param price
 * @param decimalRate tokenY/tokenX
 * @returns
 */
export function getBinPointByPrice(
  pointDelta: number,
  price: string,
  decimalRate: number,
  slotNumber: number
) {
  const point = Math.log(+price * decimalRate) / Math.log(CONSTANT_D);
  const point_int = Math.round(point);
  const point_int_bin = getBinPointByPoint(pointDelta, slotNumber, point_int);
  return point_int_bin;
}

/**
 *  calculate bin point by point
 * @param pointDelta
 * @param point
 * @param slotNumber
 * @returns
 */
export function getBinPointByPoint(
  pointDelta: number,
  slotNumber: number,
  point: number,
  type?: 'round' | 'floor' | 'ceil'
) {
  const binWidth = pointDelta * slotNumber;
  let int;
  if (type == 'floor') {
    int = Math.floor(point / binWidth);
  } else if (type == 'ceil') {
    int = Math.ceil(point / binWidth);
  } else {
    int = Math.round(point / binWidth);
  }
  const point_in_bin = int * binWidth;
  if (point_in_bin < POINTLEFTRANGE) {
    return POINTLEFTRANGE;
  } else if (point_in_bin > POINTRIGHTRANGE) {
    return 800000;
  }
  return point_in_bin;
}

export function reverse_price(price: string) {
  if (Big(price).eq(0)) return '999999999999999999999';
  return Big(1).div(price).toFixed();
}

export function getReadableTokenXAmount(
  leftPoint: number,
  rightPoint: number,
  rangeLiquidity: string,
  token: TokenMetadata
) {
  const tokenXAmount = new BigNumber(rangeLiquidity)
    .multipliedBy(
      (Math.pow(Math.sqrt(CONSTANT_D), rightPoint - leftPoint) - 1) /
        (Math.pow(Math.sqrt(CONSTANT_D), rightPoint) -
          Math.pow(Math.sqrt(CONSTANT_D), rightPoint - 1))
    )
    .toFixed();
  return toReadableNumber(token.decimals, toPrecision(tokenXAmount, 0));
}

export function getReadableTokenYAmount(
  leftPoint: number,
  rightPoint: number,
  rangeLiquidity: string,
  token: TokenMetadata
) {
  const tokenYAmount = new BigNumber(rangeLiquidity).multipliedBy(
    (Math.pow(Math.sqrt(CONSTANT_D), rightPoint) -
      Math.pow(Math.sqrt(CONSTANT_D), leftPoint)) /
      (Math.sqrt(CONSTANT_D) - 1)
  );
  const y_result = tokenYAmount.toFixed();
  return toReadableNumber(token.decimals, toPrecision(y_result, 0));
}
